import React, { useEffect, useRef, useState } from "react";
import { Box, Paper, Grid, Container, Tab, Tabs } from "@mui/material";
import debounce from "lodash/debounce";
import {
  Button,
  cellClassName,
  DataTableV2,
  defaultColDef,
  Typography,
} from "@platform/design-system";
import { ANALYST_RFPS_STATUSES } from "@platform/rfp-core";

import { useAnalystRfpsData } from "../../../hooks/useAnalystRfpsData";
import { RfpStatusFilter } from "../../../components/RfpStatusFilter";
import { SearchInput } from "../../../components";
import { ANALYST_HEADER } from "../../RFPs/utils";

export const AnalystDashboardRfpsTable: React.FC = () => {
  const [currStatus, setCurrStatus] = useState<string[]>([]);
  const [query, setQuery] = useState("");
  const [tabIndex, setTabIndex] = useState(0);
  const [debouncedQuery, setDebouncedQuery] = useState("");

  const debouncedSearch = useRef(
    debounce((value) => {
      setDebouncedQuery(value);
    }, 300),
  ).current;

  const { tabCounts, dataTableOptions, resetPage } = useAnalystRfpsData({
    debouncedSearch: debouncedQuery,
    statusFilters: currStatus,
    tabIndex,
  });

  const columns = ANALYST_HEADER.map((header) => ({
    ...defaultColDef,
    ...header,
  }));

  useEffect(() => {
    resetPage();
  }, [currStatus, resetPage]);

  const handleSearchSubmitted = (
    event: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    const newTerm = event.target.value;
    setQuery(newTerm);
    debouncedSearch(newTerm);
    resetPage();
  };

  const handleClearFilters = (): void => {
    setQuery("");
    setCurrStatus([]);
    setDebouncedQuery("");
    resetPage();
  };

  const handleChangeTab = (_, newValue): void => {
    setTabIndex(newValue);
    setQuery("");
    setCurrStatus([]);
    setDebouncedQuery("");
    resetPage();
  };

  return (
    <Container>
      <Paper elevation={3} sx={{ my: 4, p: 5 }}>
        <Typography variant="display-small" color="inherit">
          My Open RFP Projects
        </Typography>

        <Grid container mb={5}>
          <Grid
            size={12}
            sx={{ borderBottom: 1, borderColor: "divider" }}
            mt={3}
            mb={3}
          >
            <Tabs
              className="detail-page-tabs"
              value={tabIndex}
              onChange={handleChangeTab}
              aria-label="Analyst Rfp tabs"
            >
              <Tab label={`ACTIVE (${tabCounts.active})`} />
              <Tab label={`DELIVERED (${tabCounts.delivered})`} />
            </Tabs>
          </Grid>
          <Grid
            container
            size={12}
            sx={{
              [`& .${cellClassName} .ag-cell-wrapper`]: {
                display: "grid",
                alignItems: "center",
                minHeight: "100%",
              },
            }}
          >
            {
              <>
                <Box mb={2} sx={{ flexGrow: 1 }}>
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12, md: tabIndex === 0 ? 7 : 10.5 }}>
                      <SearchInput
                        placeholder="Search"
                        onChange={handleSearchSubmitted}
                        fullWidth
                        value={query}
                      />
                    </Grid>
                    <Grid
                      size={{ xs: 12, md: tabIndex === 0 ? 5 : 1.5 }}
                      display="flex"
                      alignItems="center"
                      gap={2}
                    >
                      {tabIndex === 0 && (
                        <RfpStatusFilter
                          options={ANALYST_RFPS_STATUSES}
                          currStatus={currStatus}
                          setCurrStatus={setCurrStatus}
                          selectProps={{ multiple: true }}
                        />
                      )}
                      <Button
                        variant="tertiary"
                        disabled={!query && currStatus.length === 0}
                        onClick={handleClearFilters}
                      >
                        Clear All
                      </Button>
                    </Grid>
                  </Grid>
                </Box>
                <DataTableV2
                  columnDefs={columns}
                  gridOptions={{ ...dataTableOptions }}
                />
              </>
            }
          </Grid>
        </Grid>
      </Paper>
    </Container>
  );
};
